import React from 'react';

import type { OAuthProvider } from '@/lib/api/auth';
import { cn } from '@/lib/utils';

interface OAuthProviderLogoProps {
    provider: OAuthProvider;
    className?: string;
}

export const OAUTH_PROVIDER_NAMES: Record<OAuthProvider, string> = {
    google: 'Google',
    discord: 'Discord',
    x: 'X',
    telegram: 'Telegram',
};

const GoogleLogo: React.FC<{ className?: string }> = ({ className }) => (
    <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 24 24"
        className={className}
        data-provider-logo="google"
    >
        <path
            fill="#4285F4"
            d="M21.35 12.22c0-.74-.07-1.45-.19-2.13H12v4.03h5.24a4.48 4.48 0 0 1-1.94 2.94v2.51h3.14c1.84-1.69 2.91-4.18 2.91-7.35Z"
        />
        <path
            fill="#34A853"
            d="M12 21.72c2.62 0 4.82-.87 6.44-2.35l-3.14-2.51c-.87.58-1.99.92-3.3.92-2.53 0-4.67-1.71-5.44-4.01H3.31v2.59A9.72 9.72 0 0 0 12 21.72Z"
        />
        <path
            fill="#FBBC05"
            d="M6.56 13.77A5.84 5.84 0 0 1 6.25 12c0-.61.11-1.21.31-1.77V7.64H3.31A9.72 9.72 0 0 0 2.28 12c0 1.57.38 3.05 1.03 4.36l3.25-2.59Z"
        />
        <path
            fill="#EA4335"
            d="M12 4.22c1.43 0 2.72.49 3.73 1.45l2.79-2.79A9.36 9.36 0 0 0 12 .28a9.72 9.72 0 0 0-8.69 5.36l3.25 2.59C7.33 5.93 9.47 4.22 12 4.22Z"
        />
    </svg>
);

const DiscordLogo: React.FC<{ className?: string }> = ({ className }) => (
    <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 24 24"
        className={cn('text-[#5865F2]', className)}
        data-provider-logo="discord"
    >
        <path
            fill="currentColor"
            d="M20.32 4.37A19.8 19.8 0 0 0 15.37 2.8a13.7 13.7 0 0 0-.63 1.29 18.3 18.3 0 0 0-5.48 0 13.4 13.4 0 0 0-.64-1.29A19.7 19.7 0 0 0 3.68 4.38C.55 9.05-.3 13.61.12 18.1a19.9 19.9 0 0 0 6.07 3.07 14.8 14.8 0 0 0 1.3-2.12 12.8 12.8 0 0 1-2.05-.99l.5-.39c3.95 1.83 8.24 1.83 12.14 0l.5.39c-.65.39-1.34.72-2.05.99.37.74.8 1.45 1.3 2.12a19.8 19.8 0 0 0 6.06-3.07c.5-5.2-.85-9.72-3.57-13.73ZM8.02 15.33c-1.18 0-2.16-1.1-2.16-2.45s.96-2.45 2.16-2.45 2.18 1.11 2.16 2.45c0 1.35-.96 2.45-2.16 2.45Zm7.96 0c-1.18 0-2.16-1.1-2.16-2.45s.96-2.45 2.16-2.45 2.18 1.11 2.16 2.45c0 1.35-.95 2.45-2.16 2.45Z"
        />
    </svg>
);

const XLogo: React.FC<{ className?: string }> = ({ className }) => (
    <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 24 24"
        className={className}
        data-provider-logo="x"
    >
        <path
            fill="currentColor"
            d="M18.24 2H21l-6.03 6.89L22.06 22H16.5l-4.35-5.69L7.17 22H4.4l6.45-7.37L4.06 2h5.7l3.93 5.2L18.24 2Zm-.97 17.7h1.53L8.93 4.18H7.29L17.27 19.7Z"
        />
    </svg>
);

const TelegramLogo: React.FC<{ className?: string }> = ({ className }) => (
    <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 24 24"
        className={className}
        data-provider-logo="telegram"
    >
        <circle cx="12" cy="12" r="11" fill="#229ED9" />
        <path
            fill="#fff"
            d="m17.7 7.1-1.8 10.1c-.13.72-.53.9-1.08.56l-2.75-2.03-1.33 1.28c-.15.15-.27.27-.55.27l.2-2.8 5.1-4.6c.22-.2-.05-.31-.34-.11l-6.3 3.97-2.72-.85c-.59-.18-.6-.59.12-.87l10.62-4.1c.49-.18.92.12.76.88Z"
        />
    </svg>
);

const PROVIDER_LOGOS: Record<
    OAuthProvider,
    React.FC<{ className?: string }>
> = {
    google: GoogleLogo,
    discord: DiscordLogo,
    x: XLogo,
    telegram: TelegramLogo,
};

export const OAuthProviderLogo: React.FC<OAuthProviderLogoProps> = ({
    provider,
    className,
}) => {
    const Logo = PROVIDER_LOGOS[provider];
    return <Logo className={cn('size-5', className)} />;
};

OAuthProviderLogo.displayName = 'OAuthProviderLogo';
